import { useI18n } from '@change-corgi/core/react/i18n';

import { usePromotePageState } from 'src/app/pages/promote/pageContext';
import { useLoginState } from 'src/app/shared/hooks/session';
import type { PaymentType } from 'src/app/shared/utils/payments';
import { validateEmail, validateFirstName, validateLastName, validateName } from 'src/app/shared/utils/payments';

import { useCanUserUpdateFields } from '../useCanUserUpdateFields';

import { useValidateAmount } from './hooks/useValidateAmount';

type ValidationResult = {
	valid: boolean;
	errors: Record<string, string>;
};

export function useValidationResult(): (paymentType: PaymentType) => ValidationResult {
	const { typedTranslate } = useI18n();
	const [{ email, firstName, lastName, name, usingSavedPaymentMethod }] = usePromotePageState();
	const loginState = useLoginState();
	const canUserUpdateFields = useCanUserUpdateFields();
	const validateAmount = useValidateAmount();

	return function validationResult(paymentType): ValidationResult {
		const amountValidation = validateAmount();
		let emailValidationI18nKey;
		let firstNameValidationI18nKey;
		let lastNameValidationI18nKey;
		let nameValidationI18nKey;
		if (canUserUpdateFields(paymentType)) {
			emailValidationI18nKey = validateEmail({ email, loginState });
			firstNameValidationI18nKey = validateFirstName({ firstName, loginState });
			lastNameValidationI18nKey = validateLastName({ lastName, loginState });
			nameValidationI18nKey = validateName({ name, selectedPaymentType: paymentType, usingSavedPaymentMethod });
		}

		const errors = {
			...amountValidation,
			...(emailValidationI18nKey && { email: typedTranslate(emailValidationI18nKey) }),
			...(firstNameValidationI18nKey && { firstName: typedTranslate(firstNameValidationI18nKey) }),
			...(lastNameValidationI18nKey && { lastName: typedTranslate(lastNameValidationI18nKey) }),
			...(nameValidationI18nKey && { name: typedTranslate(nameValidationI18nKey) }),
		};

		return {
			valid: !Object.values(errors).length,
			errors,
		};
	};
}
